// ==============================================
// Plan sections: the one list that says where every plan setting lives, what
// it is called, and what it means in plain words (the ⓘ beside it).
//
// To add a setting: add it to PackageFormData in packageForm.ts, then add one
// entry here in the section it belongs to. planSections.test.ts fails until
// every setting has exactly one home and an explanation.
// ==============================================

import type { PackageFormData } from './packageForm';

type Key = keyof PackageFormData;
type KeyOf<T> = { [K in Key]: PackageFormData[K] extends T ? K : never }[Key];
type BoolKey = KeyOf<boolean>;
type NumKey = KeyOf<number>;
type TextKey = KeyOf<string>;

interface Base {
  label: string;
  /** Plain-words explanation for the ⓘ: what it does, where the skipper meets it, what on and off mean. */
  info: string;
  hint?: string;
  /** Short tag beside the label: red for settings that change boats in the field, amber for ones with no effect yet. */
  badge?: string;
  badgeTone?: 'red' | 'amber';
  keys: Key[];
}

export type CustomBlock = 'features' | 'token-settings' | 'basemaps-online' | 'basemaps-offline' | 'scout-diagnostics';

export type PlanField = Base & (
  | { kind: 'toggle'; key: BoolKey }
  | { kind: 'text' | 'textarea'; key: TextKey; placeholder?: string }
  /** The plan's URL key: locked on a saved plan until the admin confirms a change. */
  | { kind: 'slug'; key: TextKey; placeholder?: string }
  | { kind: 'number'; key: NumKey; min?: number; max?: number; suffix?: string; fallback?: number; clamp?: boolean }
  | { kind: 'money'; key: NumKey; suffix?: string; quarterly?: boolean; zeroWarning?: string }
  | { kind: 'select'; key: Key; options: { value: number | string; label: string }[] }
  /** Storage chosen in GB from presets, stored in MB; greyed out while `library` is off. */
  | { kind: 'storage'; key: NumKey; library: BoolKey }
  /** A count with − and +, where 0 means unlimited. */
  | { kind: 'count'; key: NumKey; unit: string }
  | { kind: 'slider'; key: NumKey; min: number; max: number; describe: (n: number) => string }
  | { kind: 'custom'; id: CustomBlock; secondInfo?: { label: string; info: string } }
);

export interface PlanGroup { title?: string; fields: PlanField[] }

export interface PlanSection {
  id: string;
  title: string;
  /** The question this section answers, shown under its title. */
  question: string;
  danger?: boolean;
  groups: PlanGroup[];
}

type Opts = Omit<Base, 'label' | 'info' | 'keys'>;

const toggle = (key: BoolKey, label: string, info: string, o: Opts = {}): PlanField =>
  ({ kind: 'toggle', key, keys: [key], label, info, ...o });
const text = (key: TextKey, label: string, info: string, o: Opts & { placeholder?: string } = {}): PlanField =>
  ({ kind: 'text', key, keys: [key], label, info, ...o });
const number = (key: NumKey, label: string, info: string,
  o: Opts & { min?: number; max?: number; suffix?: string; fallback?: number; clamp?: boolean } = {}): PlanField =>
  ({ kind: 'number', key, keys: [key], label, info, ...o });
const select = (key: Key, label: string, info: string, options: { value: number | string; label: string }[], o: Opts = {}): PlanField =>
  ({ kind: 'select', key, keys: [key], label, info, options, ...o });
const custom = (id: CustomBlock, keys: Key[], label: string, info: string,
  o: Opts & { secondInfo?: { label: string; info: string } } = {}): PlanField =>
  ({ kind: 'custom', id, keys, label, info, ...o });

const zoomName = (level: number) => (level <= 8 ? 'Country' : level <= 12 ? 'City' : level <= 16 ? 'Street' : 'Building');

/** The storage sizes offered as buttons, in GB. */
export const STORAGE_PRESETS_GB = [1, 2, 5, 10];

const LISTED_ONLY = { badge: 'Listed only', badgeTone: 'amber' as const };
export const PLAN_SECTIONS: PlanSection[] = [
  {
    id: 'store',
    title: 'Storefront',
    question: 'How does this plan appear on the pricing page?',
    groups: [{
      fields: [
        text('name', 'Name',
          'The plan\'s name as customers see it on the pricing page, at checkout, on invoices and in their account. ' +
          'Keep it short and recognisable, like "Coastal" or "Offshore". Changing it renames the plan everywhere straight away.',
          { placeholder: 'Pro Plan' }),
        { kind: 'slug', key: 'slug', keys: ['slug'], label: 'Slug (URL key)', placeholder: 'pro',
          hint: 'Lower case, no spaces. Locked once the plan is saved.',
          info: 'A short behind-the-scenes name for the plan, like a filing reference. It appears in the web address of the checkout link (…/checkout?package=coastal), and the system looks plans up by it. ' +
            'It should be lower case with hyphens instead of spaces, such as "coastal" or "blue-water". ' +
            'A few names have special meaning in the code ("casual", "Tester"), and changing it breaks any checkout link already shared, so do not change it on a plan people are already on.' },
        { kind: 'textarea', key: 'description', keys: ['description'], label: 'Description',
          info: 'One or two sentences under the plan name on the pricing page, saying who the plan is for. ' +
            'Write it for a skipper choosing between plans, for example "For weekend sailors who stay close to shore."',
          placeholder: 'Perfect for professional maritime operations' },
        custom('features', ['features'], 'Feature bullets',
          'The ticked list of selling points shown on this plan\'s card on the pricing page, in the plan comparison and on the customer\'s current plan in MyPolaris. ' +
          'These are words only: they do not switch anything on or off. Make sure each bullet matches a setting that is actually on for this plan, ' +
          'or customers will be promised something they do not get. Drag to change the order; double-click a bullet to edit it.'),
        toggle('is_popular', 'Mark as Popular',
          'Puts a "Most Popular" badge and a highlighted border on this plan\'s card on the pricing page and on the upgrade cards in MyPolaris. ' +
          'It changes nothing about what the plan includes. Normally only one plan has it.'),
        select('visibility', 'Visibility',
          'Whether new customers can find this plan. Active: shown on the pricing page and in the plan picker, and can be bought. ' +
          'Hidden: not offered to customers, but people already on it keep it. The free default plan, templates and staff plans are kept Hidden. ' +
          'Archived: retired and kept only for the record.',
          [{ value: 'active', label: 'Active' }, { value: 'hidden', label: 'Hidden' }, { value: 'archived', label: 'Archived' }]),
        number('sort_order', 'Sort order',
          'The position of this plan on the pricing page and in admin lists. Lower numbers come first, so 0 sits before 1. ' +
          'Plans with the same number fall back to their own order.',
          { hint: 'Lower numbers show first.' }),
      ],
    }],
  },
  {
    id: 'price',
    title: 'Price & billing',
    question: 'What does it cost, and who bills it?',
    groups: [{
      fields: [
        { kind: 'money', key: 'monthly_price_cents', keys: ['monthly_price_cents'], label: 'Monthly price', quarterly: true,
          info: 'The price per month in New Zealand dollars. Customers are billed every three months, so each bill is three times this amount. ' +
            'Check with finance before changing it on a plan people are already paying for.' },
        toggle('manual_expiry', 'Manual expiry (no Stripe billing)',
          'For testers, staff and partner boats only. When on, the plan is not billed by card (Stripe), ' +
          'and its subscriptions never run out by themselves: the boat keeps its features until an admin ends it. ' +
          'Never switch this on for a plan that paying customers buy.',
          { hint: 'Use for tester or internal packages. Subscriptions on this package never expire automatically.' }),
      ],
    }],
  },
  {
    id: 'tokens',
    title: 'Tokens',
    question: 'How much can a boat use, and what happens when it runs out?',
    groups: [{
      fields: [
        number('included_tokens', 'Included tokens',
          'Tokens are the plan\'s allowance of map data: one token is one map tile, so zooming in further uses them faster. ' +
          'A boat spends them when it downloads chart areas to use offline, and on chart orders from the website. ' +
          'This is how many the plan includes each month. Set 0 and the pricing page shows the plan as "Pay-as-you-go".',
          { suffix: 'a month' }),
        { kind: 'money', key: 'cost_per_1000_tokens_cents', keys: ['cost_per_1000_tokens_cents'], label: 'Price per 1,000 extra tokens',
          info: 'What a customer pays for each 1,000 tokens beyond the monthly allowance when they order charts from the website. ' +
            'For a pay-as-you-go plan (0 included tokens) it is the headline price on the pricing page. At NZ$0, those orders are free.',
          hint: 'Website chart orders past the allowance, and the pay-as-you-go price.',
          zeroWarning: 'At NZ$0, website chart orders past the allowance are free.' },
        custom('token-settings', ['token_packs', 'out_of_tokens_policy', 'out_of_tokens_amount'], 'Top-up packs',
          'Extra bundles of tokens customers on this plan can buy when their monthly allowance runs low, like a phone data top-up. ' +
          'Each pack has a size and a price in each currency you want to sell it in; leave a currency empty to not sell in it. ' +
          'Only packs marked "On sale" are offered, under Billing in MyPolaris. Top-ups roll over and are kept even if the subscription lapses.',
          {
            secondInfo: {
              label: 'When this plan runs out',
              info: 'What happens when a boat has used every token. Not set: nothing is ever refused (how it works today). ' +
                'Stop: no new downloads until they top up. Overdraft: they carry on up to a set number of tokens, repaid from next month\'s allowance. ' +
                'Grace period: they carry on for a set number of days, time to top up once back in signal. ' +
                'Live keeps drawing: live charts keep working and only new downloads stop. ' +
                'Charts already on the boat always keep working, official ENC charts are never paused, and the skipper is shown the rule. ' +
                'It only applies to customers switched on to the new token system, which nobody is yet.',
            },
          }),
      ],
    }],
  },
  {
    id: 'maps',
    title: 'Charts & maps',
    question: 'What can the boat see, and what can it take offline?',
    groups: [
      {
        fields: [
          { kind: 'slider', key: 'max_zoom_level', keys: ['max_zoom_level'], label: 'Max zoom level', min: 0, max: 22,
            describe: (n) => `${n} · ${zoomName(n)}`, hint: 'Higher shows more detail.',
            info: 'How far in a skipper can zoom, and how detailed the chart areas they download can be. Higher numbers show more detail: around 8 shows a country, 12 a city, 16 individual streets and jetties, 18 and above single buildings. ' +
              'Each level up uses four times the map tiles, so higher zoom uses tokens much faster. The Scout enforces this limit.' },
          toggle('siitech_marine_enc', 'Siitech Marine ENC',
            'Official electronic navigation charts (ENC, Admiralty data from Siitech): the surveyed charts with depths, buoys, lights and hazards. ' +
            'When on, the Scout draws them over the water and PassageWiki shows them as a full chart. They are licensed per user per year, so each plan with this on costs us a licence.'),
          toggle('bathymetry', 'Bathymetry',
            'Shades the sea by water depth, so shallow and deep water are easy to tell apart at a glance. ' +
            'When on, the depth layers appear in the Scout\'s map layer list. When off, they are not offered.'),
          custom('basemaps-online', ['allowed_basemaps'], 'Map sources online',
            'The background maps and satellite imagery the boat may show while it has internet. Tick a source to allow it. ' +
            'Some are free (OpenStreetMap), some cost us per use (Google, Bing, Esri), and some need a key on our side. Navionics on PassageWiki is controlled by this list too.'),
        ],
      },
      {
        title: 'Offline',
        fields: [
          toggle('offline_packs', 'Offline downloads',
            'Lets skippers download areas of map before they leave, so the charts keep working out of mobile signal. ' +
            'When off, the boat can only show maps while it has internet. When on, choose below which sources may be downloaded.'),
          custom('basemaps-offline', ['allowed_basemaps_offline'], 'Map sources offline',
            'Which map sources a skipper may download for offline use. ' +
            'Some providers forbid storing their maps on a device in their terms (Google, Bing, Mapbox, HERE), so only tick those for testing. ' +
            'At least one source must be ticked while offline downloads are on.'),
        ],
      },
      {
        title: 'Other chart sources',
        fields: [
          toggle('sd_card_charts', 'SD Card Charts',
            'Lets the Scout open chart files the skipper puts on its memory card (SD card); they appear in the chart Library under "Imported". ' +
            'When off, charts on the card are not shown.'),
          toggle('google_drive_charts', 'Google Drive Charts',
            'Lets the owner link folders from their Google Drive in MyPolaris; the Scout then fetches the chart files in those folders by itself. ' +
            'When off, the Drive Charts page is not available.'),
        ],
      },
    ],
  },
  {
    id: 'weather',
    title: 'Weather',
    question: 'Which forecasts, and how far ahead?',
    groups: [
      {
        title: 'Forecast on the Scout and PassageWiki',
        fields: [
          number('weather_days', 'Forecast days',
            'How many days ahead the forecast reaches, on both the Scout and the PassageWiki website, from 0 to 16. ' +
            'At 16 every forecast model is open in full (GFS 16 days, ECMWF 15, ICON 7½). ' +
            'Every boat gets at least 2 days whatever is set here, so weather is never switched off completely.',
            { min: 0, max: 16, clamp: true, suffix: 'days', hint: '0–16 · 16 opens every model fully: GFS 16 days, ECMWF 15, ICON 7½' }),
          toggle('weather_colour', 'Colour pulldown',
            'Lets the weather panel shade the whole chart by one measure, such as wind strength, sea state, rain or pressure, on the Scout and on PassageWiki. ' +
            'When off, the Colour pulldown is locked and tapping it offers an upgrade.',
            { hint: 'Shade the chart by wind, sea, rain, pressure…' }),
        ],
      },
    ],
  },
  {
    id: 'boat',
    title: 'On the boat',
    question: 'What runs on the Scout?',
    groups: [
      {
        title: 'Navigation & apps',
        fields: [
          toggle('external_nmea', 'External NMEA sources',
            'The boat\'s own instruments (depth, wind, speed through the water, compass heading, AIS) reaching the Scout over NMEA. ' +
            'The Instrument Panel, AIS Integration and TrueHelm need it: when this is off they are off on the boat too, whatever their own switch says. ' +
            'The Scout\'s own GPS position, speed and course work on every plan.',
            { hint: 'Instrument Panel, AIS and TrueHelm need this.' }),
          toggle('instrument_panel', 'Instrument Panel',
            'The live instrument cards on the Scout\'s chart: depth, speed and heading, and wind, read from the boat\'s own instruments. ' +
            'When off, the cards show no readings and point the skipper to the pricing page. Needs External NMEA sources.'),
          toggle('ais_integration', 'AIS Integration',
            'AIS is the system ships use to broadcast who and where they are. When on, nearby vessels appear on the Scout\'s chart, which helps avoid collisions. ' +
            'When off, other vessels are not shown. Needs External NMEA sources.'),
          toggle('truehelm', 'TrueHelm',
            'Lists the TrueHelm helm display app as included in the plan; needs External NMEA sources. ' +
            'The app is not locked when this is off, so today it only changes what the plan list says; a Scout that has been out of signal for over a month does stop offering it.',
            LISTED_ONLY),
          toggle('app_library', 'App Library',
            'The "Apps on this Scout" section, where approved third-party apps are installed and kept up to date. ' +
            'When off, the section is hidden and the Scout never checks for apps.',
            { hint: 'Apps on this Scout: third-party app installs and updates.' }),
        ],
      },
      {
        title: 'Anchor',
        fields: [
          toggle('vortex_anchor', 'Vortex Anchor',
            'Lists the Vortex Anchor anchor-watch app as included in the plan. ' +
            'The app is not locked when this is off, so today it only changes what the plan list says; a Scout that has been out of signal for over a month does stop offering it.',
            LISTED_ONLY),
          toggle('anchor_remote_view', 'Anchor Watch remote view',
            'Lets someone ashore watch the boat\'s anchor watch live on their phone, from a link or a QR code shown on the Scout. ' +
            'When off, the Scout hides its QR button and MyPolaris offers an upgrade instead of Anchor Watch.',
            { hint: 'Scout QR + MyPolaris.' }),
        ],
      },
      {
        title: 'Sharing',
        fields: [
          toggle('public_tracking', 'Public Tracking',
            'Polaris Track: the owner can share where the boat is and where it has been on track.vortexnav.com, as a private link or a public listing, with a passage log. ' +
            'They choose this per Scout in MyPolaris. When off, the Tracking page is hidden and the boat cannot be shared.',
            { hint: 'Polaris Track.' }),
        ],
      },
    ],
  },
  {
    id: 'polaris',
    title: 'MyPolaris & storage',
    question: 'What do they get online, and how much room?',
    groups: [
      {
        fields: [
          { kind: 'count', key: 'max_devices', keys: ['max_devices'], label: 'Max devices', unit: 'devices',
            hint: 'Devices per subscription.',
            info: 'How many Scouts one account may register on this plan. Registering one more is refused with "Device limit reached", and MyPolaris shows "X of N devices registered". ' +
              'Most plans allow 1. Switch on Unlimited for no limit, for example for a fleet or tester plan.' },
        ],
      },
      {
        title: 'Ship\'s Papers',
        fields: [
          toggle('document_library', 'Ship\'s Papers',
            'A document library for the boat in MyPolaris and on the Scout: registration, certificates, insurance, manuals and crew papers, with a "Ready to sail" check of what is missing or expiring. ' +
            'Crew can be invited, and the boat owner\'s plan decides it for everyone aboard. When off, Ship\'s Papers is not available.'),
          { kind: 'storage', key: 'document_quota_mb', keys: ['document_quota_mb'], library: 'document_library', label: 'Ship\'s Papers storage',
            hint: 'Per vessel. Default 2 GB.',
            info: 'How much room each boat gets for Ship\'s Papers documents. It is saved in megabytes, where 1 GB is 1,024 MB. ' +
              'Uploads are refused once it is full. If someone holds several plans, the most generous one counts.' },
        ],
      },
      {
        title: 'Screenshot Library',
        fields: [
          toggle('screenshot_library', 'Screenshot Library',
            'Lets the Scout upload its screenshots to the owner\'s MyPolaris account, to look at or share later from a phone or computer. ' +
            'When off, screenshots stay on the device.'),
          { kind: 'storage', key: 'screenshot_quota_mb', keys: ['screenshot_quota_mb'], library: 'screenshot_library', label: 'Screenshot storage',
            hint: 'Per account. Default 2 GB.',
            info: 'How much room each account gets for uploaded screenshots. It is saved in megabytes, where 1 GB is 1,024 MB. ' +
              'Uploads are refused once it is full.' },
        ],
      },
      {
        title: 'Other libraries',
        fields: [
          toggle('nav_library', 'Navigation Library',
            'The skipper\'s routes, waypoints and tracks, kept in MyPolaris and sent to the boat. ' +
            'When off, the library is not available in MyPolaris. PassageWiki itself stays free either way.'),
          toggle('sd_card_manager', 'SD Card Manager',
            'Lets the owner browse and delete the files on the Scout\'s memory card (SD card) from MyPolaris, without being on the boat. It cannot upload files. ' +
            'When off, the SD Card page is not shown.'),
        ],
      },
    ],
  },
  {
    id: 'fleet',
    title: 'Testing & fleet',
    question: 'What could change real boats right now?',
    danger: true,
    groups: [
      {
        title: 'Changes Scouts in the field',
        fields: [
          toggle('google_webview', 'Google web view',
            'Switches every Scout on this plan to Google\'s own engine for drawing web pages (Android System WebView) the next time each one restarts. It is a technical setting for testing. ' +
            'It only works if that engine has already been installed from the App Library. If unsure, leave it off.',
            { badge: 'Changes Scouts', badgeTone: 'red', hint: 'Switch this plan\'s Scouts to Google\'s web view at their next restart (needs it installed from the App Library).' }),
          custom('scout-diagnostics', ['scout_diagnostics'], 'Scout diagnostics',
            'Shows extra technical detail on the Scout for support staff, such as network addresses, phone numbers, GPS internals and battery readings. ' +
            'It only appears once admin mode is also turned on on the device itself, so a normal customer never sees it. Meant for tester and staff plans.'),
        ],
      },
    ],
  },
];

/**
 * Settings kept on every plan but left off the editor because nothing acts on
 * them today (Tony, 31 Oct 2026). Saving a plan keeps their values unchanged.
 */
export const HIDDEN_SETTINGS: Partial<Record<Key, string>> = {
  weather_gfs: 'Retired: weather now comes from Forecast days and the Colour pulldown.',
  weather_ecmwf: 'Retired: weather now comes from Forecast days and the Colour pulldown.',
  weather_wavewatch: 'Retired: weather now comes from Forecast days and the Colour pulldown.',
  weather_briefing: 'Retired: the 72-hour weather card is free for everyone.',
  forecast_duration_hours: 'Retired: replaced by Forecast days, which sets the Scout and PassageWiki together.',
  vortex_status_panel: 'Retired: the status drawer in Vortex Anchor and Vortex Tide shows to everyone.',
  tide_watch: 'Not read by any Scout app.',
  vt_tide_alerts: 'Tide alerts were never finished; nothing is sent.',
  vortex_tide_prediction_days: 'Sent to the Scout but no app reads it.',
  priority_generation: 'Left over from an earlier version; nothing reads it.',
  api_access: 'Reaches the Scout as premium_charts, which nothing acts on.',
};

export const fieldsOf = (section: PlanSection): PlanField[] => section.groups.flatMap((g) => g.fields);
export const sectionKeys = (section: PlanSection): Key[] => fieldsOf(section).flatMap((f) => f.keys);
